// Grist API implementation of RecordProvider

import type { GristRecord, GristReadyOptions, ColumnMappings } from '../types';
import type { RecordProvider, RecordProviderEvents } from './record-provider';

/**
 * Grist widget column configuration
 */
const GRIST_COLUMNS: GristReadyOptions['columns'] = [
  { name: 'Name', title: 'Nom' },
  { name: 'Latitude', title: 'Latitude' },
  { name: 'Longitude', title: 'Longitude' },
  { name: 'Type', title: 'Type' },
  { name: 'Info', title: 'Informations', optional: true, allowMultiple: true },
];

/**
 * RecordProvider implementation using the real Grist API
 */
export class GristRecordProvider implements RecordProvider {
  private records: GristRecord[] = [];
  private ready = false;
  private events: RecordProviderEvents = {};

  /**
   * Initialize Grist widget and set up listeners
   */
  initialize(events: RecordProviderEvents): void {
    this.events = events;

    grist.ready({
      requiredAccess: 'read table',
      columns: GRIST_COLUMNS,
      allowSelectBy: true,
    });

    this.ready = true;
    this.setupListeners();
  }

  /**
   * Clean up - Grist doesn't provide a way to remove listeners
   */
  destroy(): void {
    this.ready = false;
    this.events = {};
  }

  /**
   * Get current records
   */
  getRecords(): GristRecord[] {
    return this.records;
  }

  /**
   * Set cursor position in Grist
   */
  async selectRecord(recordId: number): Promise<void> {
    try {
      await grist.setCursorPos({ rowId: recordId });
    } catch (e) {
      console.log('Unable to set cursor position:', e);
    }
  }

  /**
   * Save option to Grist
   */
  async setOption(key: string, value: unknown): Promise<void> {
    try {
      await grist.setOption(key, value);
    } catch (e) {
      console.log('Unable to save option:', e);
    }
  }

  /**
   * Get option from Grist
   */
  async getOption<T>(key: string): Promise<T | null> {
    try {
      return await grist.getOption<T>(key);
    } catch (e) {
      console.log('Unable to get option:', e);
      return null;
    }
  }

  /**
   * Check if provider is ready
   */
  isReady(): boolean {
    return this.ready;
  }

  /**
   * Set up Grist event listeners
   */
  private setupListeners(): void {
    grist.onRecords((records: GristRecord[], mappings?: unknown) => {
      this.records = records;
      this.events.onRecords?.(records, mappings as ColumnMappings | undefined);
    });

    grist.onRecord((record: GristRecord | null) => {
      this.events.onRecord?.(record);
    });

    grist.onOptions((options: unknown) => {
      this.events.onOptions?.(options);
    });
  }
}
